import { JSX, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ChatFormTarget, useChatFormBridge } from '../context/ChatFormBridge';
import { CapturedStamp, isReplayed } from './CapturedStamp';

/** A value the assistant has offered to put into a field on the screen behind the panel. */
export interface ChatProposal {
    field: string;
    value: string;
    why: string;
    resource: string;
    recordId: string | number | null;
    /** What the field held when the proposal was made. */
    was: string;
}

/**
 * An offer to fill in a field, with what is there now beside what it would become.
 *
 * Accepting writes the value into the form and stops. **It never saves.** The assistant is
 * read-only and stays that way: the user reads the change on the screen it belongs to and saves it
 * themselves, so validation, permissions and the activity monitor all behave exactly as they do
 * for a typed value.
 */
export const ProposalCard: (props: { proposal: ChatProposal, capturedAt?: number }) => JSX.Element =
    ({ proposal, capturedAt }): JSX.Element => {
        const bridge = useChatFormBridge();
        const [outcome, setOutcome] = useState<'open' | 'applied' | 'dismissed' | 'stale'>('open');

        function accept(): void {
            const target: ChatFormTarget | null = bridge?.current() ?? null;

            // The screen can change between a proposal being made and being accepted, and writing
            // into whatever happens to be open now would be the worst thing this feature could do.
            const sameScreen: boolean = target !== null
                && target.resource === proposal.resource
                && String(target.recordId ?? '') === String(proposal.recordId ?? '');

            if (!sameScreen || !target?.fields().includes(proposal.field)) {
                setOutcome('stale');
                return;
            }

            target.setValue(proposal.field, proposal.value);
            setOutcome('applied');
        }

        return (
            <div className={ 'chat__proposal is-' + outcome }>
                <p className="proposal__why">{ proposal.why }</p>

                <dl className="proposal__change">
                    <dt>{ proposal.field }</dt>
                    <dd className="change__was">{ proposal.was === '' ? 'empty' : proposal.was }</dd>
                    <dd className="change__now">{ proposal.value }</dd>
                </dl>

                { /* HQ-11. A replayed proposal is a record of one that was offered. Filling a
                     field with a value the assistant suggested a fortnight ago, against a record
                     that has moved on since, is the worst thing a replayed card could do - so it
                     says when it was offered and has no buttons at all. */ }
                { isReplayed(capturedAt) && (
                    <p className="proposal__replayed">
                        <CapturedStamp capturedAt={ capturedAt as number } />
                    </p>
                ) }

                { !isReplayed(capturedAt) && outcome === 'open' && (
                    <div className="proposal__actions">
                        <button type="button" className="proposal__accept" onClick={ accept }>
                            <CheckIcon fontSize="small" />
                            Fill in
                        </button>
                        <button
                            type="button"
                            className="proposal__dismiss"
                            onClick={ (): void => setOutcome('dismissed') }
                        >
                            <CloseIcon fontSize="small" />
                            No thanks
                        </button>
                    </div>
                ) }

                { outcome === 'applied' && (
                    <p className="proposal__outcome">Filled in. Nothing is saved until you press Save.</p>
                ) }
                { outcome === 'dismissed' && (
                    <p className="proposal__outcome">Left as it was.</p>
                ) }
                { outcome === 'stale' && (
                    <p className="proposal__outcome proposal__outcome--stale">
                        That screen is no longer open, so nothing was changed.
                    </p>
                ) }
            </div>
        );
    };
